import { useCallback, useMemo, useState } from "react";
import { SessionNote } from "../ProctoringVideoPlayer";
import { ProctoringResultViewProps } from "../ProctoringResultView";
import useNoteManager, { RawNote } from "./useNotesManager";
import apiClient from "../../../../../helpers/api-client";

export type AutomatedProctoringSessionNote = SessionNote & {
	id: number;
};

type useSessionNotesProps = Pick<ProctoringResultViewProps, "courseId"> & {
	firstEventDate: Date | undefined;
	sessionUuid: string;
	timeOffset: number;
};

export default function useSessionNotes({ courseId, firstEventDate, sessionUuid, timeOffset }: useSessionNotesProps) {
	const [rawSessionNotes, setRawSessionNotes] = useState<RawNote[]>([]);
	const [rawRoomScanNotes, setRawRoomScanNotes] = useState<RawNote[]>([]);

	const sessionNotesManager = useNoteManager<Date>({
		sessionUuid,
		courseId,
		setter: setRawSessionNotes,
		apis: {
			fetch: (params) => apiClient.proctoringSession.getSessionNotes(params),
			add: (params) => apiClient.proctoringSession.addSessionNote(params),
			edit: (params) => apiClient.proctoringSession.editSessionNote(params),
			delete: (params) => apiClient.proctoringSession.deleteSessionNote(params),
		},
	});

	const roomScanNotesManager = useNoteManager<number>({
		sessionUuid,
		courseId,
		setter: setRawRoomScanNotes,
		apis: {
			fetch: (params) => apiClient.proctoringSession.getRoomScanNotes(params),
			add: (params) => apiClient.proctoringSession.addRoomScanNote(params),
			edit: (params) => apiClient.proctoringSession.editRoomScanNote(params),
			delete: (params) => apiClient.proctoringSession.deleteRoomScanNote(params),
		},
	});

	const fetchNotes = async () => {
		await sessionNotesManager.refresh();
		await roomScanNotesManager.refresh();
	};

	const sessionNotes = useMemo((): AutomatedProctoringSessionNote[] => {
		if (!firstEventDate) return [];
		return rawSessionNotes
			.map((x) => ({
				id: x.id,
				content: x.content,
				time: (new Date(x.timestamp).getTime() - firstEventDate.getTime() + timeOffset) / 1000,
				date: new Date(x.timestamp),
			}))
			.sort((a, b) => a.time - b.time);
	}, [firstEventDate, rawSessionNotes, timeOffset]);

	const roomScanNotes = useMemo((): AutomatedProctoringSessionNote[] => {
		return rawRoomScanNotes
			.map((x) => {
				// Ensure time is a number.
				const timeAsNumber = Number(x.timestamp);

				return {
					id: x.id,
					content: x.content,
					time: timeAsNumber,
					date: new Date(timeAsNumber),
				};
			})
			.sort((a, b) => a.time - b.time);
	}, [rawRoomScanNotes]);

	const addNotesHandler = useCallback(
		async (content: string, timestamp: Date) => {
			if (!firstEventDate) {
				return false;
			}

			return await sessionNotesManager.add(content, timestamp);
		},
		[firstEventDate, sessionNotesManager],
	);

	const editNoteHandler = useCallback(
		async (noteId: number, content: string) => {
			return await sessionNotesManager.edit(noteId, content);
		},
		[sessionNotesManager],
	);

	const deleteNoteHandler = useCallback(
		async (noteId: number) => {
			return await sessionNotesManager.remove(noteId);
		},
		[sessionNotesManager],
	);

	const addRoomScanNoteHandler = useCallback(
		async (content: string, timestamp: number) => {
			return await roomScanNotesManager.add(content, timestamp);
		},
		[roomScanNotesManager],
	);

	const editRoomScanNoteHandler = useCallback(
		async (noteId: number, content: string) => {
			return await roomScanNotesManager.edit(noteId, content);
		},
		[roomScanNotesManager],
	);

	const deleteRoomScanNoteHandler = useCallback(
		async (noteId: number) => {
			return await roomScanNotesManager.remove(noteId);
		},
		[roomScanNotesManager],
	);

	return {
		fetchNotes,
		session: {
			addNote: addNotesHandler,
			editNote: editNoteHandler,
			deleteNote: deleteNoteHandler,
			refresh: sessionNotesManager.refresh,
			notes: sessionNotes,
		},
		roomScan: {
			addNote: addRoomScanNoteHandler,
			editNote: editRoomScanNoteHandler,
			deleteNote: deleteRoomScanNoteHandler,
			notes: roomScanNotes,
		},
	};
}
